import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../config/api';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create checkout session and redirect to Stripe
  const createCheckoutSession = useCallback(async (params, tierTypeParam, metadataParam) => {
    try {
      setLoading(true);
      setError(null);

      // Handle both old format (tripId, tierType) and new format (object)
      let tripId, tierType, metadata, billingInterval, usePartnerAuth;
      if (typeof params === 'object' && params !== null && !Array.isArray(params)) {
        // New format: {tripId, tierType, metadata, billingInterval}
        ({ tripId, tierType, metadata, billingInterval, usePartnerAuth } = params);
      } else {
        // Old format: (tripId, tierType)
        tripId = params;
        tierType = tierTypeParam || 'trip_pass';
        metadata = metadataParam;
      }

      console.log('Creating checkout session for:', { tripId, tierType, metadata, billingInterval });
      console.log('API URL:', api.defaults?.baseURL || 'No base URL set');

      const interval = billingInterval
        || metadata?.billing_interval
        || metadata?.billingInterval
        || null;
      const requestBody = { tripId, tierType };
      if (metadata) {
        requestBody.metadata = metadata;
      }
      if (interval) {
        requestBody.billingInterval = interval;
      }

      const isPartnerPlan = typeof tierType === 'string' && (
        tierType.startsWith('partner_plan_')
        || tierType === 'free'
        || tierType === 'starter'
        || tierType === 'partner_extra_seat'
        || tierType === 'partner_traveler_credits'
        || tierType === 'partner_professional_onboarding'
        || tierType === 'partner_developer_api'
      );
      const response = await api.post('/payments/create-checkout-session', requestBody, {
        ...(usePartnerAuth || isPartnerPlan ? { usePartnerAuth: true } : {})
      });

      console.log('Checkout session response:', response.data);

      // Bypass, in-place Stripe plan change, or already-on-plan — no Checkout Session.
      if (response.data.success && !response.data.clientSecret && !response.data.url) {
        return response.data;
      }

      // For embedded checkout, we return the client secret instead of redirecting
      if (response.data.clientSecret) {
        console.log('Embedded checkout client secret received');
        return response.data;
      } else if (response.data.url) {
        // Fallback to redirect mode if no client secret
        console.log('Redirecting to Stripe:', response.data.url);
        window.location.href = response.data.url;
        return response.data;
      } else {
        throw new Error('No checkout URL or client secret received from server');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      setError(error.response?.data?.error || 'Failed to create checkout session');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if user has premium access to a trip
  const checkPremiumAccess = useCallback(async (tripId) => {
    try {
      const response = await api.get(`/payments/check-access/${tripId}`);
      return { hasAccess: response.data.hasPremiumAccess, reason: 'api_check' };
    } catch (error) {
      console.error('Error checking premium access:', error);
      return { hasAccess: false, reason: 'error' };
    }
  }, []);

  // Get trip upgrade information
  const getTripUpgradeInfo = useCallback(async (tripId) => {
    try {
      const response = await api.get(`/payments/trip/${tripId}/upgrade-info`);
      return response.data;
    } catch (error) {
      console.error('Error getting trip upgrade info:', error);
      throw error;
    }
  }, []);

  // Grant access to a member (for group upgrades)
  const grantMemberAccess = useCallback(async (tripId, memberId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post(`/payments/trip/${tripId}/grant-access`, {
        memberId
      });

      return response.data;
    } catch (error) {
      console.error('Error granting member access:', error);
      setError(error.response?.data?.error || 'Failed to grant access');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get payment session details (for success page)
  const getSessionDetails = useCallback(async (sessionId) => {
    try {
      const response = await api.get(`/payments/session/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting session details:', error);
      throw error;
    }
  }, []);

  // Common function for one-time charges (scalable)
  const createOneTimeCharge = useCallback(async (serviceType, amount, description, metadata = {}) => {
    return await createCheckoutSession({
      tripId: null,
      tierType: serviceType,
      metadata: {
        service_type: serviceType,
        amount: amount,
        description: description,
        ...metadata
      }
    });
  }, [createCheckoutSession]);

  const value = {
    loading,
    error,
    createCheckoutSession,
    createOneTimeCharge,
    checkPremiumAccess,
    getTripUpgradeInfo,
    grantMemberAccess,
    getSessionDetails,
    clearError: () => setError(null)
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};
